import {
    areSearchParamsEqual,
    ROLES_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type RolesFilterItem = { key: string; value: any };

export type RolesUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterRoleId: RolesFilterItem | null;
    filterRoleName: RolesFilterItem | null;
    filterStatusId: RolesFilterItem | null;
    uiFilters: Record<string, any>;
};

export type RolesUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterRoleId: RolesFilterItem | null;
    filterRoleName: RolesFilterItem | null;
    filterStatusId: RolesFilterItem | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialRolesUrlState(): RolesUrlViewState {
    return parseRolesUrlState(getHashSearchParams());
}

export function parseRolesUrlState(params: URLSearchParams): RolesUrlViewState {
    params = unwrapEncodedSearchParams(params, ROLES_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const archiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const id = params.get('id');
    const name = params.get('name');
    const isActiveRaw = params.get('isActive');
    const isActive = isActiveRaw == null ? null : toTrueFalse(isActiveRaw);

    if (id) uiFilters.id = id;
    if (name) uiFilters.name = name;
    if (isActive != null) {
        uiFilters.active = isActive
            ? {name: 'Активна', code: true}
            : {name: 'Неактивна', code: false};
    }

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        archiveTableToggle,
        filterRoleId: toFilterItem('Id', id),
        filterRoleName: toFilterItem('Name', name),
        filterStatusId: isActive == null ? null : {key: 'Active', value: isActive},
        uiFilters,
    };
}

export function buildRolesSearchParams(state: RolesUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainRolesSearchParams(state), ROLES_ENCODED_QUERY_KEY);
}

function buildPlainRolesSearchParams(state: RolesUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.archiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'id', state.filterRoleId?.value);
    setScalar(params, 'name', state.filterRoleName?.value);
    if (state.filterStatusId?.value !== undefined && state.filterStatusId?.value !== null && state.filterStatusId.value !== '') {
        params.set('isActive', normalizeBooleanParam(state.filterStatusId.value));
    }

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function toFilterItem(key: string, value?: string | null): RolesFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toTrueFalse(value: string): boolean | null {
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
    return null;
}

function normalizeBooleanParam(value: any): string {
    if (value === true || value === 'true' || value === '1') return '1';
    if (value === false || value === 'false' || value === '0') return '0';
    if (value?.code === true) return '1';
    if (value?.code === false) return '0';
    return String(value);
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
